// O COTĂ, AȘA CUM O DESENEAZĂ O PLANȘĂ DE ARHITECTURĂ.
//
// Nu `siteGlyphs.DimSpanView`, deși amândouă desenează un segment cotat. Ăla e
// al planșei de situație — A01, unde o cotă e o afirmație rară despre lot, pusă
// peste asfalt — și e făcut ca s-o vezi de departe: bulină de 16 cm rază, cifră
// BOLD pe plăcuță albă, roșu A01. Pe un plan de etaj cu peste o mie de cote,
// aceleași alegeri dau exact ce s-a văzut pe ecran: o pastă roșie din care nu se
// mai citește nici desenul, nici cifrele.
//
// Convenția de aici e măsurată pe planșa de referință (A07, SONEX DESIGN, etaj
// curent), la care se uită oricine desenează așa ceva în practică:
//
//   * CULOARE — albastru pentru cotele perimetrale, negru pentru cele din
//     interiorul apartamentelor. Roșul nu apare nicăieri pe A07 ca linie de
//     cotă: acolo e culoarea numelui de cameră.
//   * MARCAJ DE CAPĂT — bulină rotundă plină. Fără săgeți, fără bife oblice.
//     Diametrul ei e ~0,55 din înălțimea cifrei, deci `r = fs * 0.28`.
//   * CIFRA — centrată pe segmentul ei, ALĂTURI de linie (niciodată peste),
//     rotită odată cu linia și ținută în picioare pe ecran.
//   * FĂRĂ PLĂCUȚĂ ALBĂ. Pe A07 cifrele stau direct peste desen, iar liniile
//     de sub ele nu se întrerup — inclusiv axele punctate trec prin cifră.
//     Plăcuța ar fi însemnat, la o mie de cote, o mie de dreptunghiuri albe
//     peste plan.
//   * GROSIME — cel mai subțire lucru de pe planșă: sub linia de perete.

//   * CLICK PE CIFRĂ (Oana, 20.10.2026) — cifra e singurul lucru al cotei care
//     primește clickul (`pick`, pe cifră și pe o pastilă nevăzută din jurul
//     ei, ca să se nimerească); linia și bulinele rămân `DECOR`. Cota ALEASĂ
//     se desenează aprinsă: linia în culoarea selecției, mai groasă, cu
//     liniile de capăt perpendiculare (cele pe care planșa le trage de la
//     obiect la linia de cotă) și cifra pe o plăcuță albă — ca să se vadă
//     exact ce măsoară numărul, între o mie de alte cifre.

import { memo } from "react";
import { DECOR } from "./glyphs";
import { useViewTurn } from "./siteGlyphs";
import type { DimSpan } from "./siteDerive";
import { pickAttrs } from "../inspect";

/** Albastrul cotelor perimetrale și negrul celor interioare, de pe A07. */
export const COTA_EXT = "#1c4fd8";
export const COTA_INT = "#22262b";
/** Culoarea cotei alese: a selecției, ca la orice obiect. */
const COTA_ALEASA = "var(--sel-line, #0b7f92)";

/** MEMOIZATĂ: planșa se redesenează la fiecare cadru de pan, iar o mie de
 *  cote reconciliate de fiecare dată se simțeau în scroll. Primește numai
 *  valori simple și `d` din lista memoizată a nivelului, ca `memo` să aibă
 *  ce compara; atributele de selecție se fac aici, din `level` și `eid`. */
export default memo(function Cota({ d, fs = 0.34, culoare = COTA_INT, level, eid, ales = false }: {
  d: DimSpan; fs?: number; culoare?: string;
  /** Nivelul și cheia cotei, pentru selecție; fără `level` cota nu se poate alege. */
  level?: number; eid?: string;
  /** Cota e selecția curentă: se desenează aprinsă. */
  ales?: boolean;
}) {
  const pick = level != null && eid ? pickAttrs({ kind: "cota", level, eid }) : undefined;
  const [dx, dy] = [d.x2 - d.x1, d.y2 - d.y1];
  const L = Math.hypot(dx, dy);
  if (!(L > 0)) return null;
  const [mx, my] = [(d.x1 + d.x2) / 2, (d.y1 + d.y2) / 2];
  const col = ales ? COTA_ALEASA : culoare;
  // liniile de capăt: perpendiculare pe cotă, cât două înălțimi de cifră
  const [nx, ny] = [-dy / L * fs, dx / L * fs];

  // În picioare PE ECRAN, deci rotirea vederii contează: un lanț care se citește
  // de la stânga la dreapta în repaus ajunge invers după un sfert de tură.
  const turn = useViewTurn();
  let a = (Math.atan2(dy, dx) * 180) / Math.PI;
  const peEcran = ((a + turn + 180) % 360 + 360) % 360 - 180;
  const intors = peEcran > 90 || peEcran < -90;
  if (intors) a += 180;

  // Cifra stă ALĂTURI de linie, pe partea ei, la ~0,5 din înălțimea ei — v.
  // nota de sus. `-fs*0.5` în cadrul rotit înseamnă „deasupra", fiindcă y crește
  // în jos.
  // Pastila clickului: cât cifra, puțin mai lată — lățimea e aproximată din
  // numărul de caractere, nu măsurată (`getBBox` ar costa un reflow pe mie de cote).
  const lat = Math.max(1.2, d.label.length * 0.62) * fs;
  const clic = pick?.["data-pick"] ? { ...pick, style: { ...pick.style, pointerEvents: "auto" as const } } : undefined;
  return (
    <g style={DECOR}>
      {ales && (
        <>
          <line x1={d.x1 - nx} y1={d.y1 - ny} x2={d.x1 + nx} y2={d.y1 + ny}
                stroke={col} strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
          <line x1={d.x2 - nx} y1={d.y2 - ny} x2={d.x2 + nx} y2={d.y2 + ny}
                stroke={col} strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
        </>
      )}
      <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}
            stroke={col} strokeWidth={ales ? 2.2 : 0.7}
            vectorEffect="non-scaling-stroke" />
      <circle cx={d.x1} cy={d.y1} r={fs * (ales ? 0.4 : 0.28)} fill={col} />
      <circle cx={d.x2} cy={d.y2} r={fs * (ales ? 0.4 : 0.28)} fill={col} />
      <g transform={`translate(${mx} ${my}) rotate(${a - turn})`}>
        <rect x={-lat / 2} y={-fs * 1.4} width={lat} height={fs * 1.25} rx={fs * 0.2}
              fill={ales ? "#fff" : "transparent"} stroke={ales ? col : "none"}
              strokeWidth={1} vectorEffect="non-scaling-stroke" {...clic} />
        <text x={0} y={-fs * 0.45} fontSize={fs} textAnchor="middle"
              fontWeight={ales ? 800 : 600} fill={col}
              style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }} {...clic}>
          {d.label}
        </text>
      </g>
    </g>
  );
});
